export type CompressionLevel = 'screen' | 'ebook' | 'printer'

export const LEVEL_VALUES: readonly CompressionLevel[] = ['screen', 'ebook', 'printer']

export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function savingsPercent(inputBytes: number, outputBytes: number): string {
  if (inputBytes === 0) return '0%'
  const saved = ((inputBytes - outputBytes) / inputBytes) * 100
  const sign = saved >= 0 ? '−' : '+'
  return `${sign}${Math.abs(Math.round(saved))}%`
}

export function compressedFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-compressed.pdf`
}

// ──────── Classifier ─────────────────────────────────────────────────────────
//
// Walk the PDF's image XObjects and classify by filter. The decision tree:
//   - vector-only (no image streams at all) → noop, file is already compact
//   - jpeg-dominant (≥ 80% of image bytes are DCTDecode) → WASM handles it well
//   - exotic / chart-heavy → WASM best-effort, skips the formats it cannot decode

export interface PdfClassification {
  totalPages: number
  imageStreams: number
  jpegBytes: number         // /DCTDecode
  flateRgbBytes: number     // /FlateDecode + /DeviceRGB or /DeviceGray (handled by WASM)
  otherImageBytes: number   // CCITTFax, JBIG2, indexed, CMYK, etc. (WASM can't touch)
  vectorOnly: boolean       // no image streams found
  wasmCoverageRatio: number // (jpegBytes + flateRgbBytes) / total image bytes; 1.0 if vectorOnly
}

export async function classifyPdf(file: File): Promise<PdfClassification> {
  const { PDFDocument, PDFRawStream, PDFName } = await import('pdf-lib')
  const bytes = new Uint8Array(await file.arrayBuffer())
  const doc = await PDFDocument.load(bytes, { updateMetadata: false })
  const ctx = doc.context

  let imageStreams = 0
  let jpegBytes = 0
  let flateRgbBytes = 0
  let otherImageBytes = 0

  for (const [, obj] of ctx.enumerateIndirectObjects()) {
    if (!(obj instanceof PDFRawStream)) continue
    const d = obj.dict

    const subtype = d.get(PDFName.of('Subtype'))
    if (subtype?.toString() !== '/Image') continue

    imageStreams++
    const streamLen = obj.contents.length

    const filter = d.get(PDFName.of('Filter'))?.toString() ?? ''
    const colorSpace = d.get(PDFName.of('ColorSpace'))?.toString() ?? ''

    if (filter === '/DCTDecode') {
      jpegBytes += streamLen
    } else if (filter === '/FlateDecode') {
      if (colorSpace === '/DeviceRGB' || colorSpace === '/DeviceGray') {
        flateRgbBytes += streamLen
      } else {
        otherImageBytes += streamLen
      }
    } else {
      otherImageBytes += streamLen
    }
  }

  const vectorOnly = imageStreams === 0
  const totalImageBytes = jpegBytes + flateRgbBytes + otherImageBytes
  const wasmCoverageRatio = vectorOnly
    ? 1
    : totalImageBytes === 0
      ? 1
      : (jpegBytes + flateRgbBytes) / totalImageBytes

  return {
    totalPages: doc.getPageCount(),
    imageStreams,
    jpegBytes,
    flateRgbBytes,
    otherImageBytes,
    vectorOnly,
    wasmCoverageRatio,
  }
}

// ──────── Strategy decision ──────────────────────────────────────────────────

export type Strategy =
  | { kind: 'noop'; reason: 'vector-only' }
  | { kind: 'wasm' }

export function decideStrategy(c: PdfClassification): Strategy {
  if (c.vectorOnly) return { kind: 'noop', reason: 'vector-only' }
  return { kind: 'wasm' }
}

// ──────── WASM compression ───────────────────────────────────────────────────
//
// Browser requirements: OffscreenCanvas, DecompressionStream, createImageBitmap.
// Floor = Safari 16.4 (Mar 2023). On older browsers compressFileWasm throws and
// SzhatForm shows an error.

interface LevelParams {
  quality: number
  maxLong: number
}

const LEVEL_PARAMS: Record<CompressionLevel, LevelParams> = {
  screen: { quality: 0.55, maxLong: 1100 },
  ebook: { quality: 0.70, maxLong: 1600 },
  printer: { quality: 0.85, maxLong: 2200 },
}

async function reencodeBitmap(
  bitmap: ImageBitmap,
  params: LevelParams,
): Promise<Blob> {
  const longest = Math.max(bitmap.width, bitmap.height)
  const scale = longest > params.maxLong ? params.maxLong / longest : 1
  const w = Math.max(1, Math.round(bitmap.width * scale))
  const h = Math.max(1, Math.round(bitmap.height * scale))
  const canvas = new OffscreenCanvas(w, h)
  const ctx2d = canvas.getContext('2d')
  if (!ctx2d) throw new Error('OffscreenCanvas 2d context unavailable')
  ctx2d.drawImage(bitmap, 0, 0, w, h)
  return canvas.convertToBlob({ type: 'image/jpeg', quality: params.quality })
}

async function inflateBytes(src: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate')
  const writer = ds.writable.getWriter()
  writer.write(src)
  writer.close()
  const buf = await new Response(ds.readable).arrayBuffer()
  return new Uint8Array(buf)
}

function rawToImageData(raw: Uint8Array, w: number, h: number, channels: number): ImageData {
  const imageData = new ImageData(w, h)
  const px = imageData.data
  if (channels === 3) {
    for (let i = 0, j = 0; i < raw.length; i += 3, j += 4) {
      px[j] = raw[i] ?? 0
      px[j + 1] = raw[i + 1] ?? 0
      px[j + 2] = raw[i + 2] ?? 0
      px[j + 3] = 255
    }
  } else {
    // grayscale
    for (let i = 0, j = 0; i < raw.length; i++, j += 4) {
      const v = raw[i] ?? 0
      px[j] = v
      px[j + 1] = v
      px[j + 2] = v
      px[j + 3] = 255
    }
  }
  return imageData
}

export async function compressFileWasm(
  file: File,
  level: CompressionLevel,
  signal: AbortSignal,
): Promise<Blob> {
  if (signal.aborted) throw new DOMException('aborted', 'AbortError')

  const { PDFDocument, PDFRawStream, PDFName, PDFDict, PDFNumber } = await import('pdf-lib')

  const params = LEVEL_PARAMS[level]
  const inputBytes = new Uint8Array(await file.arrayBuffer())
  const doc = await PDFDocument.load(inputBytes, { updateMetadata: false })
  const ctx = doc.context

  for (const [ref, obj] of ctx.enumerateIndirectObjects()) {
    if (signal.aborted) throw new DOMException('aborted', 'AbortError')
    if (!(obj instanceof PDFRawStream)) continue

    const d = obj.dict
    if (d.get(PDFName.of('Subtype'))?.toString() !== '/Image') continue

    // Skip images with a soft mask — the SMask is a separate XObject sized to
    // match the original image. Resizing the image without resizing the mask
    // produces broken transparency.
    if (d.get(PDFName.of('SMask'))) continue

    const filter = d.get(PDFName.of('Filter'))?.toString() ?? ''
    const orig = obj.contents

    let recodedBlob: Blob | null = null

    if (filter === '/DCTDecode') {
      // Browser-native JPEG decode
      const srcBlob = new Blob([orig], { type: 'image/jpeg' })
      const bitmap = await createImageBitmap(srcBlob)
      recodedBlob = await reencodeBitmap(bitmap, params)
      bitmap.close()
    } else if (filter === '/FlateDecode') {
      const cs = d.get(PDFName.of('ColorSpace'))?.toString() ?? ''
      const bpcObj = d.get(PDFName.of('BitsPerComponent'))
      const bpc = bpcObj instanceof PDFNumber ? bpcObj.asNumber() : 8
      const wObj = d.get(PDFName.of('Width'))
      const hObj = d.get(PDFName.of('Height'))
      const w = wObj instanceof PDFNumber ? wObj.asNumber() : 0
      const h = hObj instanceof PDFNumber ? hObj.asNumber() : 0

      if (bpc !== 8 || !w || !h) continue

      const channels = cs === '/DeviceRGB' ? 3 : cs === '/DeviceGray' ? 1 : 0
      if (!channels) continue

      // zlib-decompress via DecompressionStream
      let raw: Uint8Array
      try {
        raw = await inflateBytes(orig)
      } catch {
        continue
      }
      if (raw.length < w * h * channels) continue

      // Build ImageBitmap from raw pixels via ImageData + OffscreenCanvas
      const imageData = rawToImageData(raw, w, h, channels)
      const tmp = new OffscreenCanvas(w, h)
      const tmpCtx = tmp.getContext('2d')
      if (!tmpCtx) continue
      tmpCtx.putImageData(imageData, 0, 0)
      const bitmap = await createImageBitmap(tmp)
      recodedBlob = await reencodeBitmap(bitmap, params)
      bitmap.close()
    } else {
      continue
    }

    if (!recodedBlob) continue

    const recoded = new Uint8Array(await recodedBlob.arrayBuffer())
    if (recoded.length >= orig.length) continue // safety net: keep original

    // Probe new dimensions
    const probeBmp = await createImageBitmap(recodedBlob)
    const newW = probeBmp.width
    const newH = probeBmp.height
    probeBmp.close()

    // Rebuild dict: preserve everything except Width/Height/Length/Filter/DecodeParms
    const newDict = PDFDict.withContext(ctx)
    for (const [k, v] of d.entries()) {
      const ks = k.toString()
      if (
        ks === '/Length' ||
        ks === '/Width' ||
        ks === '/Height' ||
        ks === '/DecodeParms'
      ) {
        continue
      }
      newDict.set(k, v)
    }
    newDict.set(PDFName.of('Width'), ctx.obj(newW))
    newDict.set(PDFName.of('Height'), ctx.obj(newH))
    newDict.set(PDFName.of('Length'), ctx.obj(recoded.length))
    newDict.set(PDFName.of('Filter'), PDFName.of('DCTDecode'))

    ctx.assign(ref, PDFRawStream.of(newDict, recoded))
  }

  const out = await doc.save({ useObjectStreams: true })
  return new Blob([out], { type: 'application/pdf' })
}
